import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { familyPath } from '@/app/equipmentTypes/FamiliesTab';
import { equipmentTypeFamilyOfState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { EquipmentTypeFamily, EquipmentTypeId } from '@rock/types';
import { type FC, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

/** A type's family and the types it brings in beyond `listed`, if any. */
export const TypeFamilyLine: FC<{
    typeId: EquipmentTypeId;
    typeTitles: Map<EquipmentTypeId, string>;
    /** Types not to list as family members — ones already listed beside this one. */
    listed?: readonly EquipmentTypeId[];
}> = ({ typeId, typeTitles, listed = [] }) => {
    const [family] = useLoadableAtomValue(useMemo(() => equipmentTypeFamilyOfState(typeId), [typeId]));
    const members = family?.typeIds.filter((other) => other !== typeId && !listed.includes(other)) ?? [];
    if (!family || members.length === 0) return null;
    return <FamilyLine family={family} members={members} typeTitles={typeTitles} />;
};

/** A family, linked, with the member types beside it. `ruledOut` members are shown muted, with why on hover. */
export const FamilyLine: FC<{
    family: EquipmentTypeFamily;
    members: readonly EquipmentTypeId[];
    typeTitles: Map<EquipmentTypeId, string>;
    ruledOut?: readonly EquipmentTypeId[];
}> = ({ family, members, typeTitles, ruledOut = [] }) => (
    <div className="flex flex-wrap items-center gap-1.5 text-sm">
        <span className="text-muted-foreground">Family</span>
        <RouterLink to={familyPath(family.id)} className="font-medium text-primary underline-offset-4 hover:underline">
            {family.name}
        </RouterLink>
        {members.map((member) => {
            const title = typeTitles.get(member);
            const isRuledOut = ruledOut.includes(member);
            return (
                <EquipmentTypeBadge
                    key={member}
                    typeId={member}
                    title={isRuledOut ? `${title ?? member} (ruled out, still asked)` : title}
                    className={
                        isRuledOut ? 'bg-transparent text-muted-foreground ring-1 ring-inset ring-border' : undefined
                    }
                />
            );
        })}
    </div>
);
